import { H1 } from "@/components/Typography";
import Small from "@/components/Typography/Small";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from "@fortawesome/react-fontawesome";
import { VariantProps, tv } from "tailwind-variants";

const styles = tv({
  slots: {
    base: "text-left",
    featureContainer: "mb-10 flex flex-row items-center gap-10",
    feature: "font-semibold uppercase",
    banner: "mb-10 !text-banner font-bold",
    heading: "mb-10 font-bold",
    description: "!text-base",
  },
  variants: {
    align: {
      left: {
        base: "text-left",
        featureContainer: "justify-start",
      },
      center: {
        base: "text-center",
        featureContainer: "justify-center",
      },
      right: {
        base: "text-right",
        featureContainer: "justify-end",
      },
    },
    featureTextColor: {
      primary: {
        feature: "text-primary",
      },
      secondary: {
        feature: "text-secondary",
      },
      white: {
        feature: "text-white",
      },
    },
    bannerTextColor: {
      primary: {
        banner: "text-primary",
      },
      secondary: {
        banner: "text-secondary",
      },
      white: {
        banner: "text-white",
      },
    },
    headingTextColor: {
      primary: {
        heading: "text-primary",
      },
      secondary: {
        heading: "text-secondary",
      },
      white: {
        heading: "text-white",
      },
    },
    descriptionTextColor: {
      primary: {
        description: "text-primary",
      },
      secondary: {
        description: "text-secondary",
      },
      white: {
        description: "text-white",
      },
    },
  },
  defaultVariants: {
    align: "left",
    featureTextColor: "primary",
    bannerTextColor: "secondary",
    headingTextColor: "secondary",
    descriptionTextColor: "secondary",
  },
});

interface IProps {
  align?: VariantProps<typeof styles>["align"];
  featureTextIcon?: IconDefinition;
  featureTextIconProps?: Omit<FontAwesomeIconProps, "icon">;
  featureText?: string;
  featureTextColor?: VariantProps<typeof styles>["featureTextColor"];
  bannerText?: string;
  bannerTextColor?: VariantProps<typeof styles>["bannerTextColor"];
  headingText?: string;
  headingTextColor?: VariantProps<typeof styles>["headingTextColor"];
  descriptionText?: string;
  descriptionTextColor?: VariantProps<typeof styles>["descriptionTextColor"];
  customHtml?: JSX.Element;
  customHtmlPosition?: "top" | "middle" | "bottom";
}

const HeaderTextBlock = ({
  align,
  featureTextIcon,
  featureTextIconProps,
  featureText,
  featureTextColor,
  bannerText,
  bannerTextColor,
  headingText,
  headingTextColor,
  descriptionText,
  descriptionTextColor,
  customHtml,
  customHtmlPosition,
}: IProps) => {
  const { base, featureContainer, feature, banner, heading, description } =
    styles({
      align,
      featureTextColor,
      headingTextColor,
      descriptionTextColor,
      bannerTextColor,
    });

  const renderFeatureSection = () => {
    if (
      !!featureText ||
      !!featureTextIcon ||
      (!!customHtml && customHtmlPosition === "top")
    )
      return (
        <div className={featureContainer()}>
          {!!featureTextIcon && (
            <FontAwesomeIcon
              className={feature()}
              icon={featureTextIcon}
              {...featureTextIconProps}
            />
          )}

          {!!customHtml && customHtmlPosition === "top" && customHtml}

          {!!featureText && <Small className={feature()}>{featureText}</Small>}
        </div>
      );
  };

  return (
    <div className={base()}>
      {renderFeatureSection()}

      {!!customHtml && customHtmlPosition === "middle" && customHtml}

      {!!bannerText && <p className={banner()}>{bannerText}</p>}

      {!!headingText && <H1 className={heading()}>{headingText}</H1>}

      {!!descriptionText && <p className={description()}>{descriptionText}</p>}

      {!!customHtml && customHtmlPosition === "bottom" && customHtml}
    </div>
  );
};

export default HeaderTextBlock;
